import React, { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import type { AppLayoutOutletContext } from './AppLayoutOutletContext'
import { toast } from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../context/NotificationContext'
import { useAttendance } from '../context/AttendanceContext'
import { useSection } from '../context/SectionContext'
import CheckInScreen from '../components/attendance/CheckInScreen'
import AttendanceDropdown from '../components/attendance/AttendanceDropdown'
import { api } from '../api/client'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { ROLES, isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { isSidebarSectionVisible } from '@/features/dashboard/sections'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@jira-clone/ui'
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  Kanban,
  Clock,
  CalendarCheck,
  Target,
  LogOut,
  Plus,
  Square,
  Users,
  GitFork,
  Briefcase,
  UserCheck,
  BadgeDollarSign,
  Archive,
  Globe,
  FileText,
  Layers,
  Bell,
  ChevronRight,
  ChevronLeft,
  Menu,
  X,
  ArrowLeft,
  Receipt,
  FileCheck,
  DollarSign,
  CreditCard,
  CheckCheck,
  Trash2,
  Volume2,
  VolumeX,
  RefreshCw,
  Settings,
  Tags,
  Search,
  Magnet,
  Handshake,
  BookMarked,
  Database,
  Users2,
  UserSearch,
  Rocket,
  GitBranch,
  GanttChart,
  Coins,
  ScrollText,
  Banknote,
  Contact,
  Bot,
  Sparkles,
  Award,
  CalendarDays,
  Megaphone,
  Network,
  LifeBuoy,
  MessageSquare,
} from 'lucide-react'
import { NotificationRow } from '@/components/notifications/NotificationRow'
import { usePermissions } from '../shared/hooks/usePermissions'

export default function AppLayout() {
  const { user, organization, logout, isLoading: isAuthLoading } = useAuth()
  const { status: attendanceStatus, isLoading: isAttendanceLoading } = useAttendance()
  const {
    notifications,
    unreadCount,
    connect,
    disconnect,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAllNotifications,
    connected,
    chimeMuted,
    toggleChime,
  } = useNotifications()
  const { selectedSection, setSelectedSection } = useSection()
  const isSuperAdmin = isSuperAdminOrOwner(user?.role)
  const effectiveSelectedSection = isSuperAdmin ? selectedSection : null
  const [notifTab, setNotifTab] = useState<'all' | 'unread'>('all')
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = usePermissions()

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [projects, setProjects] = useState<any[]>([])
  const [statuses, setStatuses] = useState<any[]>([])
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskDesc, setNewTaskDesc] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [selectedStatusId, setSelectedStatusId] = useState('')
  const [isCreatingTask, setIsCreatingTask] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('zynx_sidebar_collapsed') === 'true'
    } catch {
      return false
    }
  })
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)

  const orgId = organization?.id || (organization as any)?._id

  useEffect(() => {
    try {
      localStorage.setItem('zynx_sidebar_collapsed', String(isCollapsed))
    } catch { }
  }, [isCollapsed])

  useEffect(() => {
    setIsMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isMobileNavOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileNavOpen(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isMobileNavOpen])

  const loadMeta = async () => {
    if (!orgId) return
    try {
      const [pData, sData] = await Promise.all([
        api.get<any>(`/organizations/${orgId}/projects`),
        api.get<any>(`/organizations/${orgId}/statuses`),
      ])
      if (Array.isArray(pData)) setProjects(pData)
      else if (pData?.data) setProjects(pData.data)
      if (Array.isArray(sData)) setStatuses(sData)
      else if (sData?.data) setStatuses(sData.data)
    } catch (err) {
      console.error('Error loading meta:', err)
    }
  }

  useEffect(() => {
    if (orgId) {
      loadMeta()
      connect(orgId)
    }
    return () => disconnect()
  }, [orgId])

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTaskTitle || !selectedProjectId) return
    setIsCreatingTask(true)
    try {
      await api.post(
        `/tasks`,
        {
          projectId: selectedProjectId,
          title: newTaskTitle,
          description: newTaskDesc,
          statusId: selectedStatusId || undefined,
        },
        { skipErrorToast: true }
      )
      setNewTaskTitle('')
      setNewTaskDesc('')
      setIsTaskModalOpen(false)
      window.dispatchEvent(new CustomEvent('taskCreated'))
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'create tasks'))
    } finally {
      setIsCreatingTask(false)
    }
  }

  const overviewItem = {
    label: 'Overview',
    href: '/app',
    iconName: 'workspace',
    icon: LayoutDashboard,
    end: true,
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-950/50',
    permission: { module: 'DASHBOARD', action: 'VIEW' },
  }

  const projectNavItems = [
    { label: 'Spaces', href: '/spaces', iconName: 'folder', icon: FolderKanban, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-950/50', permission: { module: 'SPACES', action: 'VIEW' } },
    { label: 'Portfolio Timeline', href: orgId ? `/app/organizations/${orgId}/portfolio-timeline` : '/app/portfolio-timeline', iconName: 'timeline', icon: GanttChart, color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-950/50', permission: { module: 'PROJECTS', action: 'VIEW' } },
  ]

  const peopleCoreSubsections = [
    { label: 'Team Directory', href: '/app/people', iconName: 'users', icon: Users2, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
    { label: 'Organization', href: '/app/hr/organization', iconName: 'assignee', icon: Network, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
  ]

  const peopleHiringSubsections = [
    { label: 'Recruitment', href: '/app/hr/recruitment', iconName: 'source', icon: UserSearch, color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
    { label: 'Offer Letters', href: '/app/hr/offer-letters', iconName: 'document', icon: FileCheck, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
    { label: 'Onboarding', href: '/app/hr/onboarding', iconName: 'rocket', icon: Rocket, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
  ]

  const peopleTimeSubsections = [
    { label: 'Daily Attendance', href: '/app/attendance', iconName: 'assignee', icon: CalendarCheck, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
    { label: 'Leave Requests', href: '/app/leaves', iconName: 'database', icon: CalendarDays, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
  ]

  const peoplePerformanceSubsections = [
    { label: 'Performance', href: '/app/hr/performance', iconName: 'timeline', icon: Award, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
    { label: 'Incentives', href: '/app/hr/incentives', iconName: 'coins', icon: Coins, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
    { label: 'Payroll', href: '/app/hr/payroll', iconName: 'document', icon: Receipt, color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
  ]

  // Open to everyone signed in: the server limits each person to their own tickets unless they run the desk.
  const serviceDeskItem = { label: 'Service Desk', href: '/app/service-desk', iconName: 'rocket', icon: LifeBuoy, color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-950/50' }

  const peopleDocumentSubsections = [
    { label: 'Announcements', href: '/app/announcements', iconName: 'book', icon: Megaphone, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/50', permission: { module: 'PEOPLE', action: 'VIEW' } },
  ]

  const hrNavItems = [
    ...peopleCoreSubsections,
  ]

  const settingsItem = {
    label: 'Settings',
    href: '/app/settings',
    iconName: 'settings',
    icon: Settings,
    color: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-800/60',
    minRole: 'ADMIN' as const,
  }

  // Everyone logs their own time, so this is for every department (the Timesheets permission is on by default).
const timesheetItem = { label: 'Timesheets', href: '/app/timesheets', iconName: 'timeline', icon: Clock, color: 'text-fuchsia-600 dark:text-fuchsia-400', bg: 'bg-fuchsia-50 dark:bg-fuchsia-950/50', permission: { module: 'TIMESHEETS', action: 'VIEW' } }

// Notepad - available to all roles with NOTES.VIEW permission
const notepadItem = { label: 'Notepad', href: '/app/notepad', iconName: 'file-text', icon: FileText, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950/50', permission: { module: 'NOTES', action: 'VIEW' } }

// Chat is for everyone signed in - the server lists only the conversations this person belongs to.
const chatItem = { label: 'ZYNX Chat', href: '/app/chat', icon: MessageSquare, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950/50' }

const baseNavItems = [
    overviewItem,
    ...projectNavItems,
    timesheetItem,
    notepadItem,
    chatItem,
    serviceDeskItem,
  ]

  const systemNavItems = [
    ...peopleCoreSubsections,
    settingsItem,
  ]

  const leadSubsections = [
    { label: 'All Leads', href: '/app/leads', iconName: 'database', icon: Database, end: true, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/50', permission: { module: 'LEADS', action: 'VIEW' } },
    // ADMIN-tier and above only - it breaks down the whole team's leads by
    // rep, which MEMBER/VIEWER shouldn't see since they're restricted to
    // their own leads everywhere else (apps/api/src/routes/goodday/leads.ts).
    { label: 'Leads by Assignee', href: '/app/leads/assignee', iconName: 'assignee', icon: Users2, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/50', permission: { module: 'LEADS', action: 'VIEW' }, minRole: 'ADMIN' as const },
    { label: 'Leads By Source', href: '/app/leads/source', iconName: 'source', icon: UserSearch, color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/50', permission: { module: 'LEADS', action: 'VIEW' } },
    { label: 'Archive', href: '/app/leads/archive', iconName: 'archive', icon: Archive, color: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800/60', permission: { module: 'LEADS', action: 'VIEW' } },
  ]

  const dealSubsections = [
    { label: 'Active Deals', href: '/app/deals', iconName: 'database', icon: Database, end: true, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/50', permission: { module: 'DEALS', action: 'VIEW' } },
    { label: 'Pipeline', href: '/app/deals/pipeline', iconName: 'rocket', icon: Rocket, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950/50', permission: { module: 'DEALS', action: 'VIEW' } },
    { label: 'Timeline', href: '/app/deals/timeline', iconName: 'timeline', icon: GitBranch, color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-950/50', permission: { module: 'DEALS', action: 'VIEW' } },
    { label: 'Archive', href: '/app/deals/archive', iconName: 'archive', icon: Archive, color: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800/60', permission: { module: 'DEALS', action: 'VIEW' } },
  ]

  const salesDocsSubsections = [
    { label: 'Sales Docs', href: '/sales-docs', iconName: 'book', icon: BookMarked, end: true, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/50', permission: { module: 'SALES_DOCS', action: 'VIEW' } },
  ]

  const salesSubsections = [
    { label: 'Quotations', href: '/app/sales/quotations', iconName: 'document', icon: Receipt, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/50', permission: { module: 'QUOTATIONS', action: 'VIEW' } },
    { label: 'Proforma Invoices', href: '/app/sales/proforma-invoices', iconName: 'document', icon: ScrollText, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/50', permission: { module: 'PROFORMA_INVOICES', action: 'VIEW' } },
    { label: 'Payment Schedule', href: '/app/sales/milestones', iconName: 'coins', icon: Banknote, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-950/50', permission: { module: 'SALES_DOCS', action: 'VIEW' } },
    { label: 'Tax Invoices', href: '/app/sales/tax-invoices', iconName: 'document', icon: FileCheck, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/50', permission: { module: 'INVOICE_REQUESTS', action: 'VIEW' } },
    { label: 'Contacts', href: '/app/sales/contacts', iconName: 'users', icon: Contact, color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/50', permission: { module: 'SALES_DOCS', action: 'VIEW' } },
    { label: 'Lead Scraper', href: '/app/sales/lead-scraper', iconName: 'rocket', icon: Bot, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950/50', permission: { module: 'LEADS', action: 'VIEW' }, excludeDepartments: ['accounts', 'finance'] },
    { label: 'Deletion Approvals', href: '/app/sales/deletion-approvals', iconName: 'trash', icon: Trash2, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/50', permission: { module: 'LEADS', action: 'DELETE' }, minRole: 'SUPERADMIN' as const },
  ]

  if (isAuthLoading || isAttendanceLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground font-medium">Connecting to workspace...</p>
        </div>
      </div>
    )
  }

  if (attendanceStatus === 'not_checked_in') {
    return <CheckInScreen />
  }

  const isSpaceRoute = location.pathname.startsWith('/spaces')
  const isSpaceDetail = isSpaceRoute && location.pathname !== '/spaces'
  const isEffectiveCollapsed = isCollapsed && !isMobileNavOpen

  const isNavItemVisible = (item: any) => {
    // HR screens whose API is not built yet stay out of the menu (see HRMS_PLAN.md). Set
    // VITE_SHOW_UNFINISHED_HR=true to see them while developing.
    if (item.soon && import.meta.env.VITE_SHOW_UNFINISHED_HR !== 'true') return false

    const userDept = (user?.department || '').toLowerCase()
    const userRole = (user?.role || '').toLowerCase()
    const isAccountsUser = userDept === 'accounts' || userDept === 'finance' || userRole === 'accounts'

    // Accounts department should never see Lead Scraper, nor should Lead Scraper appear under Accounts section focus
    if (item.href === '/app/sales/lead-scraper' && (isAccountsUser || effectiveSelectedSection === 'accounts')) {
      return false
    }

    if (item.excludeDepartments) {
      const excluded = item.excludeDepartments.map((d: string) => d.toLowerCase())
      if (excluded.includes(userDept) || excluded.includes(userRole)) {
        return false
      }
    }

    if (
      item.permission &&
      !can(item.permission.module, item.permission.action) &&
      !(item.permission.module === 'PEOPLE' && can('USERS', item.permission.action)) &&
      // Accounts can view payroll too (the server decides what they can see).
      !(item.href === '/app/hr/payroll' && can('ACCOUNTS', 'VIEW'))
    ) {
      return false
    }
    if (item.minRole === 'ADMIN' && !isSuperAdminOrOwner(user?.role) && user?.role !== ROLES.ADMIN) return false
    if (item.minRole === 'SUPERADMIN' && !isSuperAdminOrOwner(user?.role)) return false
    return true
  }

  const renderSectionHeader = (
    title: string,
    badge?: { text: string; className: string },
    iconName?: string
  ) => {
    if (isEffectiveCollapsed) {
      return (
        <div className="hidden md:flex flex-col items-center justify-center py-2 px-1">
          <div className="flex items-center gap-1 text-muted-foreground/80">
            {iconName && (
              <div
                className="custom-icon-url"
                style={{
                  width: 14,
                  height: 14,
                  backgroundImage: `url("/skin/common/color-icons/${iconName}.svg")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
            )}
            <span className="text-[10px] font-bold tracking-tight uppercase">
              {title.slice(0, 2)}
            </span>
          </div>
        </div>
      )
    }

    return (
      <div className="pt-3 pb-1 first:pt-0">
        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 flex items-center justify-between">
          <span className="flex items-center gap-2">
            {iconName && (
              <div
                className="custom-icon-url"
                style={{
                  width: 14,
                  height: 14,
                  backgroundImage: `url("/skin/common/color-icons/${iconName}.svg")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  flexShrink: 0,
                }}
              />
            )}
            <span>{title}</span>
          </span>
          {badge && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold ${badge.className}`}>
              {badge.text}
            </span>
          )}
        </div>
      </div>
    )
  }

  const renderNavItem = (item: any) => {
    const Icon = item.icon
    if (!isNavItemVisible(item)) return null

    const checkActive = (isActive: boolean) => {
      if (item.href === '/spaces') return isSpaceRoute
      return isActive
    }

    if (isEffectiveCollapsed) {
      return (
        <Tooltip key={item.href} delayDuration={50}>
          <TooltipTrigger asChild>
            <div className="w-full flex justify-center py-1">
              <NavLink
                to={item.href}
                end={item.end}
                className={({ isActive }) => {
                  const active = checkActive(isActive)
                  return `group flex items-center justify-center p-0.5 rounded-full transition-all duration-200 ${
                    active
                      ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-sm'
                      : 'hover:scale-105'
                  }`
                }}
              >
                {({ isActive }) => {
                  const active = checkActive(isActive)
                  return item.iconName ? (
                    <div
                      className="custom-icon-url transition-transform duration-200 group-hover:scale-110"
                      style={{
                        width: 20,
                        height: 20,
                        backgroundImage: `url("/skin/common/color-icons/${item.iconName}.svg")`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Icon
                      className={`h-5 w-5 shrink-0 transition-transform duration-200 group-hover:scale-110 ${
                        active ? 'text-primary-foreground' : item.color || 'text-muted-foreground'
                      }`}
                    />
                  )
                }}
              </NavLink>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12} className="hidden md:flex items-center gap-1.5 font-medium shadow-md">
            <span>{item.label}</span>
          </TooltipContent>
        </Tooltip>
      )
    }

    return (
      <NavLink
        key={item.href}
        to={item.href}
        end={item.end}
        className={({ isActive }) => {
          const active = checkActive(isActive)
          return `group flex items-center gap-3 px-3 text-sm font-medium rounded-xl transition-all duration-200 ${
            active
              ? 'bg-primary text-primary-foreground shadow-sm font-semibold'
              : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
          }`
        }}
      >
        {({ isActive }) => {
          const active = checkActive(isActive)
          return (
            <>
              <div
                className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 ${
                  active
                    ? 'bg-white/20 text-white'
                    : `${item.bg || 'bg-muted/60'} group-hover:scale-105`
                }`}
              >
                {item.iconName ? (
                  <div
                    className="custom-icon-url"
                    style={{
                      width: 16,
                      height: 16,
                      backgroundImage: `url("/skin/common/color-icons/${item.iconName}.svg")`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Icon
                    className={`h-4 w-4 shrink-0 transition-colors ${
                      active ? 'text-white' : item.color || 'text-muted-foreground'
                    }`}
                  />
                )}
              </div>
              <span className={`truncate ${active ? 'text-primary-foreground font-semibold' : 'text-foreground/90 group-hover:text-foreground'}`}>
                {item.label}
              </span>
            </>
          )
        }}
      </NavLink>
    )
  }

  const isDepartmentSelection = isSuperAdmin && !selectedSection

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-background overflow-hidden">
        {!isDepartmentSelection && isMobileNavOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setIsMobileNavOpen(false)}
            aria-hidden="true"
          />
        )}

        <aside
          className={`fixed inset-y-0 left-0 z-50 border-r border-border bg-card flex flex-col justify-between shrink-0 transform transition-all duration-300 ease-in-out ${
            isMobileNavOpen ? 'translate-x-0' : '-translate-x-full'
          } md:relative md:translate-x-0 overflow-hidden ${
            isDepartmentSelection
              ? 'md:w-0 md:opacity-0 md:pointer-events-none md:border-r-0'
              : `md:opacity-100 ${isCollapsed ? 'md:w-20' : 'md:w-64'} md:border-r`
          }`}
        >
          {!isDepartmentSelection && (
            <button
              onClick={() => setIsCollapsed((v) => !v)}
              className="hidden md:flex absolute -right-3 top-20 z-20 h-6 w-6 items-center justify-center rounded-full border border-border bg-card shadow-sm hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
            </button>
          )}

          <div className={`flex-1 overflow-y-auto overflow-x-hidden sidebar-scrollbar transition-[width] duration-300 ${isCollapsed ? 'w-20' : 'w-64'}`}>
            <div className={`h-14 flex items-center gap-3 px-4 border-b border-border sticky top-0 bg-card z-10 ${isCollapsed ? 'md:justify-center md:px-0' : ''}`}>
              <img
                src="/zynx-mark.png"
                alt="Zynx"
                className={`h-9 w-auto shrink-0 hidden ${isCollapsed ? 'md:block' : ''}`}
              />
              <img
                src="/zynx-logo.png"
                alt="Zynx"
                className={`h-9 w-auto object-contain object-left shrink-0 min-w-0 ${isCollapsed ? 'md:hidden' : ''}`}
              />
              <button
                onClick={() => setIsMobileNavOpen(false)}
                className="md:hidden ml-auto p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                title="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className={`space-y-1 transition-all duration-200 ${isEffectiveCollapsed ? 'p-2' : 'p-3'}`}>
              {isSuperAdmin && selectedSection && selectedSection !== 'company' && !isEffectiveCollapsed && (
                <div className="mx-1 mb-2.5 px-2.5 py-1.5 rounded-xl bg-muted/60 border border-border/60 flex items-center justify-between text-xs">
                  <span className="font-semibold text-[11px] text-foreground flex items-center gap-1.5 capitalize">
                    <span className="h-2 w-2 rounded-full bg-primary" />
                    {selectedSection} Focus
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedSection(null)}
                    className="text-[10px] font-semibold text-primary hover:underline transition-colors cursor-pointer"
                    title="Reset to All Modules"
                  >
                    Show All
                  </button>
                </div>
              )}

              {(!isSuperAdmin || selectedSection === 'company') ? (
                <>
                  {renderSectionHeader('Workspace', undefined, 'workspace')}
                  {baseNavItems.map(renderNavItem)}

                  {leadSubsections.some(isNavItemVisible) && (
                    <>
                      {renderSectionHeader('Leads', { text: 'CRM', className: 'bg-primary/10 text-primary' }, 'magnet')}
                      {leadSubsections.map(renderNavItem)}
                    </>
                  )}

                  {dealSubsections.some(isNavItemVisible) && (
                    <>
                      {renderSectionHeader('Deals', { text: 'Sales', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' }, 'handshake')}
                      {dealSubsections.map(renderNavItem)}
                    </>
                  )}

                  {salesDocsSubsections.some(isNavItemVisible) && (
                    <>
                      {renderSectionHeader('Sales Docs', { text: 'Docs', className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' }, 'book')}
                      {salesDocsSubsections.map(renderNavItem)}
                    </>
                  )}

                  {salesSubsections.some(isNavItemVisible) && (
                    <>
                      {renderSectionHeader('Sales', { text: 'Ops', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' }, 'coins')}
                      {salesSubsections.map(renderNavItem)}
                    </>
                  )}

                  {(peopleCoreSubsections.some(isNavItemVisible) || peopleHiringSubsections.some(isNavItemVisible) || peopleTimeSubsections.some(isNavItemVisible)) && (
                    <>
                      {renderSectionHeader('People', { text: 'HRMS', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' }, 'users')}
                      {peopleCoreSubsections.map(renderNavItem)}
                      {peopleHiringSubsections.map(renderNavItem)}
                      {peopleTimeSubsections.map(renderNavItem)}
                      {peoplePerformanceSubsections.map(renderNavItem)}
                      {peopleDocumentSubsections.map(renderNavItem)}
                    </>
                  )}

                  {settingsItem && isNavItemVisible(settingsItem) && (
                    <>
                      {renderSectionHeader('System', undefined, 'settings')}
                      {renderNavItem(settingsItem)}
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Department Focus Navigation */}
                  {renderNavItem(overviewItem)}
                  {isNavItemVisible(timesheetItem) && renderNavItem(timesheetItem)}

                  {effectiveSelectedSection === 'sales' && (
                    <>
                      {leadSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Leads', { text: 'CRM', className: 'bg-primary/10 text-primary' }, 'magnet')}
                          {leadSubsections.map(renderNavItem)}
                        </>
                      )}

                      {dealSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Deals', { text: 'Sales', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' }, 'handshake')}
                          {dealSubsections.map(renderNavItem)}
                        </>
                      )}

                      {salesDocsSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Sales Docs', { text: 'Docs', className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' }, 'book')}
                          {salesDocsSubsections.map(renderNavItem)}
                        </>
                      )}

                      {salesSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Sales', { text: 'Ops', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' }, 'coins')}
                          {salesSubsections.map(renderNavItem)}
                        </>
                      )}
                    </>
                  )}

                  {effectiveSelectedSection === 'accounts' && (
                    <>
                      {salesSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Sales', { text: 'Ops', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' }, 'coins')}
                          {salesSubsections.map(renderNavItem)}
                        </>
                      )}
                    </>
                  )}

                  {effectiveSelectedSection === 'projects' && (
                    <>
                      {projectNavItems.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Projects', undefined, 'folder')}
                          {projectNavItems.map(renderNavItem)}
                        </>
                      )}
                    </>
                  )}

                  {(effectiveSelectedSection === 'people' || effectiveSelectedSection === 'hr') && (
                    <>
                      {peopleCoreSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('People', { text: 'Core', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' }, 'users')}
                          {peopleCoreSubsections.map(renderNavItem)}
                        </>
                      )}

                      {peopleHiringSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Hiring', { text: 'Talent', className: 'bg-rose-500/10 text-rose-600 dark:text-rose-400' }, 'rocket')}
                          {peopleHiringSubsections.map(renderNavItem)}
                        </>
                      )}

                      {peopleTimeSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Attendance', { text: 'Time', className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' }, 'assignee')}
                          {peopleTimeSubsections.map(renderNavItem)}
                        </>
                      )}

                      {peoplePerformanceSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Performance', { text: 'Growth', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' }, 'coins')}
                          {peoplePerformanceSubsections.map(renderNavItem)}
                        </>
                      )}

                      {peopleDocumentSubsections.some(isNavItemVisible) && (
                        <>
                          {renderSectionHeader('Documents', { text: 'Ops', className: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' }, 'templates')}
                          {peopleDocumentSubsections.map(renderNavItem)}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </nav>
          </div>
        </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 border-b border-border px-4 md:px-6 flex items-center justify-between shrink-0 bg-card/60 backdrop-blur-sm">
          <div className="flex items-center gap-3 min-w-0">
            {isDepartmentSelection ? (
              <div className="flex items-center gap-2.5">
                <img
                  src="/zynx-logo.png"
                  alt="Zynx"
                  className="h-8 w-auto object-contain"
                />
                <span className="text-muted-foreground/30 text-sm hidden sm:inline">/</span>
                <span className="text-xs font-medium text-muted-foreground hidden sm:inline">
                  Workspaces
                </span>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden -ml-2 shrink-0"
                onClick={() => setIsMobileNavOpen(true)}
                title="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}

            {isSuperAdmin && selectedSection && (
              <button
                type="button"
                onClick={() => {
                  setSelectedSection(null)
                  navigate('/app')
                }}
                className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 cursor-pointer active:scale-95"
                title="Return to Workspaces"
              >
                <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-150 group-hover:-translate-x-0.5" />
                <span>Back to Workspaces</span>
              </button>
            )}
          </div>

            <div className="flex items-center gap-3 shrink-0">
              <AttendanceDropdown />
              <Popover open={notifOpen} onOpenChange={setNotifOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" title="Notifications">
                    <Bell className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                {/* Rendered via a portal to <body> with a very high z-index, so
                    no ancestor (sticky headers, overflow-hidden containers,
                    etc.) can ever clip or cover it. */}
                <PopoverContent
                  align="end"
                  sideOffset={8}
                  className="w-[calc(100vw-2rem)] max-w-96 p-0 z-[9999]"
                >
                  <div className="p-3 border-b border-border flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                      {unreadCount > 0 && (
                        <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5 shrink-0">
                          {unreadCount} new
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Reconnect"
                        onClick={() => organization?.id && connect(organization.id)}
                      >
                        <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title={chimeMuted ? 'Unmute notification chime' : 'Mute notification chime'}
                        onClick={toggleChime}
                      >
                        {chimeMuted ? (
                          <VolumeX className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <Volume2 className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </Button>
                      {unreadCount > 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Mark all as read"
                          onClick={() => markAllAsRead()}
                        >
                          <CheckCheck className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      )}
                      {notifications.length > 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Delete all notifications"
                          aria-label="Delete all notifications"
                          onClick={() => {
                            if (window.confirm('Delete all notifications? They will be removed from this list.')) clearAllNotifications()
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 px-3 pt-2">
                    {(['all', 'unread'] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setNotifTab(tab)}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${notifTab === tab
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                      >
                        {tab === 'all' ? `All (${notifications.length})` : `Unread (${unreadCount})`}
                      </button>
                    ))}
                  </div>

                  <div className="max-h-96 overflow-y-auto mt-1">
                    {(() => {
                      const visible = (notifTab === 'unread' ? notifications.filter((n) => !n.isRead) : notifications).slice(0, 20)
                      if (visible.length === 0) {
                        return (
                          <div className="p-6 text-center text-sm text-muted-foreground">
                            {notifTab === 'unread' ? 'No unread notifications' : 'No notifications'}
                          </div>
                        )
                      }
                      return visible.map((notif) => (
                        <NotificationRow
                          key={notif.id}
                          notification={notif}
                          onDelete={() => dismissNotification(notif.id)}
                          onOpen={() => {
                            markAsRead(notif.id)
                            // Deep-link to the specific record rather than
                            // the generic list - every real notification sets
                            // entityType/entityId (see notifyUser() in the
                            // route files), actionUrl is only a fallback for
                            // notification types with no entity (or an
                            // external link, e.g. a meeting URL).
                            if (notif.entityType === 'lead' && notif.entityId) {
                              navigate(`/app/leads?leadId=${notif.entityId}`)
                            } else if (notif.entityType === 'deal' && notif.entityId) {
                              navigate(`/app/deals?dealId=${notif.entityId}`)
                            } else if (notif.entityType === 'invoiceRequest' && notif.entityId) {
                              navigate(`/app/sales/invoice-requests?documentId=${notif.entityId}`)
                            } else if (notif.entityType === 'paymentSchedule' && notif.entityId) {
                              navigate(`/app/sales/milestones?scheduleId=${notif.entityId}`)
                            } else if (notif.entityType === 'announcement' && notif.entityId) {
                              navigate(notif.actionUrl || `/app/announcements?id=${notif.entityId}`)
                            } else if (notif.actionUrl) {
                              navigate(notif.actionUrl)
                            }
                            setNotifOpen(false)
                          }}
                        />
                      ))
                    })()}
                  </div>

                  <div className="p-2 border-t border-border flex items-center justify-between text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                      Chime Audio: {chimeMuted ? 'Muted' : 'On'}
                    </span>
                    <span>Click item to view record</span>
                  </div>
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="h-8 w-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-xs uppercase cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all shrink-0 select-none ml-2">
                    {user?.name ? user.name.slice(0, 2) : user?.email?.slice(0, 2) || 'U'}
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 mt-1 z-[9999]">
                  <div className="px-2 py-1.5 mb-1">
                    <p className="text-sm font-medium leading-none truncate">{user?.name || 'User'}</p>
                    <p className="text-xs leading-none text-muted-foreground mt-1 truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/app/profile')}>
                    <UserCheck className="mr-2 h-4 w-4" />
                    <span>My Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer text-destructive focus:text-destructive" onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main
            className={`relative flex-1 min-h-0 ${
              isSpaceDetail
                ? 'p-0 bg-canvas overflow-hidden flex flex-col'
                : 'overflow-y-auto p-6 bg-canvas'
            }`}
          >
            <Outlet context={{ openMobileNav: () => setIsMobileNavOpen(true) } satisfies AppLayoutOutletContext} />
          </main>
        </div>

        <Dialog open={isTaskModalOpen} onOpenChange={setIsTaskModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4 mt-2">
              <div className="space-y-1">
                <Label htmlFor="taskTitle">Task Title</Label>
                <Input
                  id="taskTitle"
                  placeholder="What needs to be done?"
                  required
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="projectSelect">Project</Label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId} required>
                  <SelectTrigger id="projectSelect">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.key})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {statuses.length > 0 && (
                <div className="space-y-1">
                  <Label htmlFor="statusSelect">Status</Label>
                  <Select value={selectedStatusId} onValueChange={setSelectedStatusId}>
                    <SelectTrigger id="statusSelect">
                      <SelectValue placeholder="Select a status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="taskDesc">Description (Optional)</Label>
                <Textarea
                  id="taskDesc"
                  placeholder="Add context, acceptance criteria, or details..."
                  rows={3}
                  value={newTaskDesc}
                  onChange={(e) => setNewTaskDesc(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsTaskModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={isCreatingTask}>
                  Create Task
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}
